import * as THREE from "three";

export class ParticlesTrail {

    constructor(params) {

        this.scene = params.scene
        this.name = params.name || "pt"+Math.floor(Math.random()*1000),
        this.particle = params.particle || this.createBaseParticle()
        this.count = params.count || 2000
        this.duration = params.duration || 2

        this.positions = []
        this.rotations = []
        this.scales = []
        this.delays = []
        this.globalTimes = []
        this.flyingTimes = []
        this.hasEnded = []
        this.isAlive = []

        this.targetScale = new THREE.Vector3(.1,.1,.1)
        this.startPosition = new THREE.Vector3()
        this.endPosition = new THREE.Vector3()

        this.tempPosition = new THREE.Vector3()
        
        this.initArrays()

        this.dummy = new THREE.Object3D();

        this.aliveParticles = this.count;

        this.createParticles()

    }

    initArrays(){
        for (let i = 0; i < this.count; i++) {
            this.positions[i] = new THREE.Vector3()
            this.rotations[i] = new THREE.Vector3()
            this.scales[i] = new THREE.Vector3()
            this.globalTimes[i] = 0
            this.flyingTimes[i] = 0
            this.delays[i] = 0
            this.hasEnded[i] = 0
            this.isAlive[i] = 1
        }
    }


    createBaseParticle(){
        const geom = new THREE.PlaneGeometry(.1,.1)
        const mat = new THREE.MeshBasicMaterial({
            color: 0xffffff, 
            side:THREE.DoubleSide, 
            transparent:true,
            opacity: .3,
            blending: THREE.AdditiveBlending,
        })
        const particle = new THREE.Mesh(geom, mat)
        return particle
    }

    createParticles() {
        const geometryParticle = this.particle.geometry.clone();
        const materialParticle = this.particle.material.clone();
        this.particles = new THREE.InstancedMesh(geometryParticle, materialParticle, this.count);
        this.scene.add(this.particles)
    }

    updateParticles(dt) {

        if (!this.emitter || !this.destination) return;
        // no more particles alive
        if (this.aliveParticles === 0){
            return;
        }

        this._destination.updateMatrixWorld();
        this.endPosition = this._destination.localToWorld(new THREE.Vector3());

        for (let i = 0; i < this.count; i++) {
            
            // particle has been marked as ended
            if (this.hasEnded[i] === 1) { 
                continue
            }
            
            // particle has died
            if (this.isAlive[i] === 0){ 
                continue
            }
            
            // particle waiting to be launched
            if (this.globalTimes[i] < this.delays[i]) { 
                this.globalTimes[i] += dt
                continue
            }

            // particle has just ended
            if (this.flyingTimes[i] > this.duration){
                this.killParticle(i)
                continue;
            }

            // progress 0 -> 1 
            let progress = this.flyingTimes[i] / this.duration
            progress = Math.sin(progress * Math.PI/2);
            
            const newScale = this.calculateVectorProgress(this.scales[i], this.targetScale, progress, {x:0, y:0, z:0}); 
            this.dummy.scale.copy(newScale)

            this.dummy.position.copy(
                this.calculateVectorProgress(this.positions[i], this.endPosition, progress)
            )
            this.dummy.rotation.x = this.rotations[i].x + dt * 2;
            this.dummy.rotation.y = this.rotations[i].y + dt * 2;
            this.dummy.rotation.z = this.rotations[i].z + dt * 2;
            
            this.dummy.updateMatrix();
            this.particles.setMatrixAt(i, this.dummy.matrix);
            
            this.rotations[i].copy(this.dummy.rotation)
            this.globalTimes[i] += dt
            this.flyingTimes[i] += dt
        }
        this.particles.instanceMatrix.needsUpdate = true

    }

    calculateVectorProgress(start, end, t, wave = {x:1, y:0, z:1}){
        let pos = new THREE.Vector3();
        pos.x = this.calculateProgress(start.x, end.x, t, wave.x);
        pos.y = this.calculateProgress(start.y, end.y, t, wave.y);
        pos.z = this.calculateProgress(start.z, end.z, t, wave.z);
        return pos;
    }
    calculateProgress(start, end, t, wave = 0){
        return start+(end-start)*t + Math.sin(t * 3 * Math.PI) * wave;
    }

    killParticle(id){
        this.hasEnded[id] = 1;
        this.isAlive[id] = 0
        this.globalTimes[id] = 0
        this.flyingTimes[id] = 0
        this.dummy.scale.set(0,0,0)
        this.dummy.updateMatrix();
        this.particles.setMatrixAt(id, this.dummy.matrix);
        this.scales[id].copy(this.dummy.scale);
        this.aliveParticles--;
        if (this.aliveParticles <= 0){
            //console.log("no more particles")
        }
    }

    resetParticle(id) {

        const tau = 6.28
        
        // position
        
        this.positions[id].copy(this.startPosition)
        this.positions[id].add(new THREE.Vector3(
            -.5 + Math.random(),
            -.5 + Math.random(),
            -.5 + Math.random()
        ).multiplyScalar(3))

        // rotation
        this.rotations[id].x = Math.random() * tau
        this.rotations[id].y = Math.random() * tau
        this.rotations[id].z = Math.random() * tau

        // scales
        this.scales[id].x = 1 + Math.random() * 1
        this.scales[id].y = 1 + Math.random() * 1
        this.scales[id].z = 1 + Math.random() * 1

        this.delays[id] = (id + Math.random()*10 ) / 300;
        this.isAlive[id] = 1
        this.hasEnded[id] = 0
        this.globalTimes[id] = 0
        this.flyingTimes[id] = 0
    }

    launchTrail({emitter, destination}){
        this.emitter = emitter
        this.destination = destination
        this.aliveParticles = this.count
        for (let i = 0; i < this.count; i++) {
            this.resetParticle(i)
        }
    }
    
    set emitter(obj){
        this._emitter = obj;
        this._emitter.updateMatrixWorld();
        this.startPosition = this._emitter.localToWorld(new THREE.Vector3());
    }
    
    set destination(obj){
        this._destination = obj;
        this._destination.updateMatrixWorld();
        this.endPosition = this._destination.localToWorld(new THREE.Vector3());
    }

    get emitter(){
        return this._emitter
    }

    get destination(){
        return this._destination
    }

}